/**
 * `entities/knowledge-base` TanStack Query key factory.
 *
 * Single source of truth for KB cache keys so the read hooks
 * (`useKnowledgeBasesList`, `useKnowledgeBaseDetail`, `useKnowledgeBaseDetailFiles`)
 * and the mutation invalidations (create/edit/delete, file upload/delete) can't
 * drift onto incompatible literals — partial-match invalidation only works while
 * the keys share a prefix.
 *
 * Tuple shapes reproduce the pre-factory ad-hoc keys verbatim so cached queries
 * keep hitting after the migration.
 *
 * Surface:
 *   - `KNOWLEDGE_BASES_QUERY_KEYS.all`                  → ['knowledgeBases']
 *   - `KNOWLEDGE_BASES_QUERY_KEYS.list(wsId, internal)` → ['knowledgeBases', wsId, internal]
 *   - `KNOWLEDGE_BASES_QUERY_KEYS.detail(id)`           → ['knowledgeBase', id]
 *   - `KNOWLEDGE_BASES_QUERY_KEYS.workspaces(id)`       → ['knowledgeBase', id, 'workspaces']
 */
export const KNOWLEDGE_BASES_QUERY_KEYS = {
  /** Root namespace for the list cache; busting it refetches every workspace/scope variant. */
  all: ['knowledgeBases'] as const,

  /** Workspace-scoped list (matches legacy ['knowledgeBases', workspaceId, includeInternal]). */
  list: (workspaceId?: string | null, includeInternal?: boolean) =>
    ['knowledgeBases', workspaceId, includeInternal] as const,

  /**
   * Single KB by id (matches legacy ['knowledgeBase', id]). The singular root is
   * intentionally distinct from the plural list namespace. The detail files key
   * is nested under this tuple, so busting it also refetches the files table.
   */
  detail: (id: string | null) => ['knowledgeBase', id] as const,

  /** The workspaces a KB is shared with. Nested under `detail`, so a detail bust refetches it. */
  // eslint-disable-next-line i18next/no-literal-string -- cache key segment, not user-facing
  workspaces: (id: string) => ['knowledgeBase', id, 'workspaces'] as const,
} as const;
